import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { crmApi, formatAED } from "@/lib/crm";
import { useSettings, stageBadgeClass } from "@/contexts/SettingsContext";
import { TrendingUp, Users, Sparkles, Trophy, XCircle, PoundSterling, CalendarClock, Percent, DollarSign, Layers } from "lucide-react";
import { format } from "date-fns";

function KpiCard({ label, value, icon: Icon, testid, subtle }) {
  return (
    <div data-testid={testid} className="p-5 border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950">
      <div className="flex items-center justify-between">
        <div className="text-[11px] tracking-overline uppercase text-neutral-500">{label}</div>
        {Icon && <Icon size={14} className="text-neutral-400" />}
      </div>
      <div className={`mt-3 font-serif text-3xl tabular-nums ${subtle ? "text-neutral-500" : ""}`}>{value}</div>
    </div>
  );
}

function Bar({ v, max }) {
  const pct = max > 0 ? Math.max(4, (v / max) * 100) : 0;
  return (
    <div className="h-2 bg-neutral-100 dark:bg-neutral-900 overflow-hidden">
      <div className="h-full bg-neutral-900 dark:bg-white" style={{ width: `${pct}%` }} />
    </div>
  );
}

export default function Dashboard() {
  const { settings } = useSettings();
  const stages = settings.stages || [];
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    crmApi.dashboard().then(setData).catch((e) => setErr(e.message));
  }, []);

  if (err) return <div className="p-8 text-rose-600">{err}</div>;
  if (!data) return <div className="p-8 text-xs tracking-overline uppercase text-neutral-500">Loading dashboard…</div>;

  const k = data.kpis;
  const maxMonth = Math.max(1, ...data.charts.monthly.map((m) => m.leads));

  return (
    <div className="p-6 md:p-10">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-[11px] tracking-overline uppercase text-neutral-500">Overview</div>
          <h1 className="mt-2 font-serif text-4xl md:text-5xl">Dashboard</h1>
        </div>
        <div className="text-xs text-neutral-500">Last updated {format(new Date(), "d MMM yyyy · HH:mm")}</div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <KpiCard testid="kpi-total-leads" label="Total Leads" value={k.total_leads} icon={Users} />
        <KpiCard testid="kpi-new-this-month" label="New This Month" value={k.new_this_month} icon={Sparkles} />
        <KpiCard testid="kpi-active-clients" label="Active Clients" value={k.active_clients} icon={TrendingUp} />
        <KpiCard testid="kpi-closed-won" label="Closed Won" value={k.closed_won} icon={Trophy} />
        <KpiCard testid="kpi-closed-lost" label="Closed Lost" value={k.closed_lost} icon={XCircle} />
        <KpiCard testid="kpi-total-revenue" label="Total Revenue" value={formatAED(k.total_revenue)} icon={DollarSign} />
        <KpiCard testid="kpi-month-revenue" label="Revenue This Month" value={formatAED(k.revenue_this_month)} icon={CalendarClock} />
        <KpiCard testid="kpi-pipeline" label="Pipeline Value" value={formatAED(k.pipeline_value)} icon={Layers} />
        <KpiCard testid="kpi-conversion" label="Conversion Rate" value={`${k.conversion_rate.toFixed(1)}%`} icon={Percent} />
        <KpiCard testid="kpi-avg-deal" label="Avg Deal Value" value={formatAED(k.average_deal_value)} icon={PoundSterling} />
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Monthly leads */}
        <div className="lg:col-span-2 border border-neutral-200 dark:border-neutral-800 p-6 bg-white dark:bg-neutral-950">
          <div className="flex items-center justify-between mb-6">
            <div className="text-[11px] tracking-overline uppercase text-neutral-500">Leads by month · last 6 months</div>
          </div>
          <div className="flex items-end gap-4 h-56">
            {data.charts.monthly.map((m) => {
              const h = maxMonth > 0 ? Math.max(6, (m.leads / maxMonth) * 100) : 0;
              return (
                <div key={m.month} className="flex-1 flex flex-col items-center gap-2" data-testid={`month-${m.month}`}>
                  <div className="w-full flex items-end justify-center h-full">
                    <div className="w-full max-w-[64px] bg-neutral-900 dark:bg-white transition-all" style={{ height: `${h}%` }} title={`${m.leads} leads`} />
                  </div>
                  <div className="text-xs tabular-nums">{m.leads}</div>
                  <div className="text-[10px] tracking-overline uppercase text-neutral-500">{m.month}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Pipeline by stage */}
        <div className="border border-neutral-200 dark:border-neutral-800 p-6 bg-white dark:bg-neutral-950">
          <div className="text-[11px] tracking-overline uppercase text-neutral-500 mb-6">Pipeline by stage</div>
          <div className="space-y-3">
            {data.charts.by_stage.map((s) => (
              <div key={s.stage} className="flex items-center gap-3">
                <span className={`text-[10px] tracking-overline uppercase px-2 py-1 border w-52 truncate ${stageBadgeClass(stages.find(x => x.label === s.stage)?.color)}`}>{s.stage}</span>
                <div className="flex-1"><Bar v={s.count} max={Math.max(...data.charts.by_stage.map((x) => x.count))} /></div>
                <span className="text-sm tabular-nums w-8 text-right">{s.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Lead sources */}
        <div className="border border-neutral-200 dark:border-neutral-800 p-6 bg-white dark:bg-neutral-950">
          <div className="text-[11px] tracking-overline uppercase text-neutral-500 mb-6">Lead sources</div>
          <div className="space-y-3">
            {data.charts.by_source.slice(0, 8).map((s) => (
              <div key={s.source} className="flex items-center gap-3">
                <span className="text-sm w-48 truncate">{s.source}</span>
                <div className="flex-1"><Bar v={s.count} max={Math.max(...data.charts.by_source.map((x) => x.count))} /></div>
                <span className="text-sm tabular-nums w-8 text-right">{s.count}</span>
              </div>
            ))}
            {data.charts.by_source.length === 0 && <div className="text-sm text-neutral-500">No data yet.</div>}
          </div>
        </div>

        {/* Upcoming tasks */}
        <div className="border border-neutral-200 dark:border-neutral-800 p-6 bg-white dark:bg-neutral-950">
          <div className="flex items-center justify-between mb-6">
            <div className="text-[11px] tracking-overline uppercase text-neutral-500">Upcoming tasks</div>
            <Link to="/crm/tasks" className="text-[11px] tracking-overline uppercase border-b border-neutral-900 dark:border-white pb-0.5">See all</Link>
          </div>
          {data.upcoming_tasks.length === 0 ? (
            <div className="text-sm text-neutral-500">No upcoming tasks.</div>
          ) : (
            <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
              {data.upcoming_tasks.map((t) => (
                <li key={t.id} className="py-3">
                  <Link to={`/crm/leads/${t.lead_id}`} className="block">
                    <div className="text-sm font-medium">{t.title}</div>
                    <div className="text-xs text-neutral-500 mt-0.5">
                      {t.lead_name || "—"} · Due {t.due_date ? format(new Date(t.due_date), "d MMM") : "no date"}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
